/**
 * Date formatting utilities for admin screens
 * Matches admin-web date formatting patterns
 */

/**
 * Parse a date-only value correctly. Due dates are stored as UTC midnight
 * ISO strings (e.g. "2026-03-26T00:00:00.000Z"). When parsed with new Date(),
 * the local time can shift to the previous day in timezones behind UTC.
 * This helper extracts the UTC date components and creates a local Date
 * representing the same calendar day.
 */
export const parseDateOnly = (date: string | Date): Date => {
  if (typeof date !== 'string') return date;
  const d = new Date(date);
  if (isNaN(d.getTime())) return d;
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

/**
 * Format date in dd-mm-yyyy format (matching admin-web)
 */
export const formatDateDDMMYYYY = (date: string | Date | null | undefined): string => {
  if (!date) return '--';

  const dateObj = parseDateOnly(date);

  if (isNaN(dateObj.getTime())) return '--';

  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();

  return `${day}-${month}-${year}`;
};

/**
 * Format date for display in task rows (short format)
 */
export const formatTaskDate = (date: string | Date | null | undefined): string => {
  if (!date) return 'No due date';

  const dateObj = parseDateOnly(date);

  if (isNaN(dateObj.getTime())) return 'Invalid date';

  return dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format date for detailed views (long format matching admin-web)
 */
export const formatTaskDateLong = (date: string | Date | null | undefined): string => {
  if (!date) return '--';

  const dateObj = parseDateOnly(date);

  if (isNaN(dateObj.getTime())) return '--';

  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

// Format date only (no time), locale-aware
export const formatDateOnly = (date: string | Date | null | undefined): string => {
  if (!date) return '--';
  const dateObj = parseDateOnly(date);
  if (isNaN(dateObj.getTime())) return '--';
  return dateObj.toLocaleDateString('en-US');
};

// Format month/year like "September 2025"
export const formatMonthYear = (month?: number, year?: number): string => {
  if (!month || !year) return 'Select Month';
  try {
    return new Date(year, month - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
  } catch {
    return 'Select Month';
  }
};

/**
 * Get due date color based on how close the due date is (matching admin-web logic)
 */
export const getDueDateColor = (dueDate: string | Date | null | undefined): string => {
  if (!dueDate) return '#9CA3AF'; // gray-400

  const dateObj = parseDateOnly(dueDate);
  if (isNaN(dateObj.getTime())) return '#9CA3AF';

  // Compare at local-day resolution: parseDateOnly already returns local-midnight,
  // so today must also be normalized to local-midnight to avoid same-day showing
  // as overdue once the wall clock passes 00:00.
  const now = new Date();
  const todayDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const daysUntilDue = (dateObj.getTime() - todayDay.getTime()) / (1000 * 60 * 60 * 24);

  if (daysUntilDue < 0) return '#F87171'; // red-400 - overdue
  if (daysUntilDue < 5) return '#FB923C'; // orange-400 - due soon
  return '#34D399'; // green-300 - plenty of time
};

/**
 * Get due date progress info (matching admin-web logic)
 */
export const getDueDateInfo = (
  dueDate: string | Date | null | undefined,
  taskStatus: string
): { progress: number; isLongOverdue: boolean } => {
  if (!dueDate || taskStatus === 'completed') {
    return { progress: 0, isLongOverdue: false };
  }

  const dateObj = parseDateOnly(dueDate);
  if (isNaN(dateObj.getTime())) {
    return { progress: 0, isLongOverdue: false };
  }

  // Compare at local-day resolution (see getDueDateColor for rationale).
  const now = new Date();
  const todayDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const totalDays = 30; // Show progress over a 30-day window
  const daysLeft = (dateObj.getTime() - todayDay.getTime()) / (1000 * 60 * 60 * 24);
  const isLongOverdue = daysLeft < -30;

  if (isLongOverdue) {
    return { progress: 1, isLongOverdue: true };
  }

  const progress = Math.min(Math.max((totalDays - daysLeft) / totalDays, 0), 1);
  return { progress, isLongOverdue: false };
};

export const formatDateShort = (date: Date | null): string => {
  if (!date) return 'No Details Found';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const formatUtcDate = (date: Date): string => {
  return date.toLocaleDateString(undefined, {
    timeZone: 'UTC',
  });
};